import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Captured by Grace — engagement photo & video gallery.
 *
 * Photos live in MongoDB and are curated via the admin dashboard at /admin
 * (Gallery tab). The public site fetches them from `/api/gallery/photos`,
 * which returns items already sorted by (chapter order, in-chapter order).
 */

const Gallery = () => {
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [slideshow, setSlideshow] = useState(false);
  const videoRef = useRef(null);

  // Fetch photos from the API on mount
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/gallery/photos`)
      .then((res) => { if (alive) setPhotos(res.data || []); })
      .catch((err) => { console.error('Gallery load failed:', err); });
    return () => { alive = false; };
  }, []);

  const openAt = (i) => {
    setIndex(i);
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setSlideshow(false);
  };
  const next = useCallback(() => setIndex((i) => (i + 1) % photos.length), [photos.length]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + photos.length) % photos.length), [photos.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === ' ') { e.preventDefault(); setSlideshow((s) => !s); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, next, prev]);

  // Slideshow auto-advance (paused automatically when a video is active)
  useEffect(() => {
    if (!open || !slideshow) return;
    if (photos[index]?.type === 'video') return;
    const id = setInterval(next, 3800);
    return () => clearInterval(id);
  }, [open, slideshow, next, index]);

  // Lock scroll while lightbox is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Touch / swipe nav
  const [swipeStartX, setSwipeStartX] = useState(0);
  const onTouchStart = (e) => { setSwipeStartX(e.touches[0].clientX); };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - swipeStartX;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next();
      else prev();
    }
  };

  // Disable context menu (right-click save) on gallery items for privacy
  const blockContext = (e) => e.preventDefault();

  const current = photos[index] || null;

  return (
    <section id="gallery" className="section bg-cream" data-testid="gallery-section">
      <div className="max-w-6xl mx-auto px-4">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12 sm:mb-14"
        >
          <p className="font-cursive text-[#C8A951] text-2xl mb-2">A Curated Collection</p>
          <h2
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#3B2F2F] tracking-tight"
            data-testid="gallery-heading"
          >
            Captured by Grace
          </h2>
          <div className="gold-divider" />
          <p
            className="italic text-[#5A4A4A] mt-4 max-w-xl mx-auto font-serif"
            data-testid="gallery-subtitle"
          >
            &ldquo;Every photograph tells a story of faith, timing, and love.&rdquo;
          </p>
        </motion.div>

        {/* Compact photo-grid — 3 cols mobile · 4 tablet · 5 desktop */}
        <div className="captured-grid" data-testid="captured-grid">
          {photos.map((item, i) => {
            const thumb = item.thumb || item.url;
            const isVideo = item.type === 'video';
            return (
              <motion.button
                key={item.url}
                type="button"
                onClick={() => openAt(i)}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ duration: 0.55, delay: (i % 10) * 0.03, ease: [0.22, 1, 0.36, 1] }}
                className="captured-tile"
                aria-label={isVideo ? `Play video: ${item.alt}` : `View photo: ${item.alt}`}
                data-testid={`captured-item-${i}`}
              >
                <picture>
                  {item.webp && !isVideo && <source srcSet={item.webp} type="image/webp" />}
                  <img
                    src={thumb}
                    alt={item.alt}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    onContextMenu={blockContext}
                    className="captured-tile-img"
                  />
                </picture>
                {isVideo && (
                  <>
                    <span className="captured-tile-play" aria-hidden="true">
                      <Play className="w-5 h-5" fill="currentColor" />
                    </span>
                    {item.duration && (
                      <span className="captured-tile-duration" aria-hidden="true">
                        {item.duration}
                      </span>
                    )}
                  </>
                )}
                <span className="captured-tile-frame" aria-hidden="true" />
              </motion.button>
            );
          })}
        </div>

        {/* Quiet footer line — keeps the theme without inviting sharing */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: 0.3 }}
          className="captured-motto text-center text-[#5A4A4A] text-sm tracking-[0.32em] uppercase font-serif"
        >
          Grace Made Us · Purpose Unites Us
        </motion.p>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open && current && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="captured-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Photo viewer"
            data-testid="captured-lightbox"
            onClick={(e) => { if (e.target === e.currentTarget) close(); }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {/* Close */}
            <button
              type="button"
              onClick={close}
              className="captured-lb-btn captured-lb-close"
              aria-label="Close gallery"
              data-testid="lightbox-close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Slideshow toggle */}
            <button
              type="button"
              onClick={() => setSlideshow((s) => !s)}
              className={`captured-lb-btn captured-lb-slideshow ${slideshow ? 'is-on' : ''}`}
              aria-label={slideshow ? 'Pause slideshow' : 'Start slideshow'}
              aria-pressed={slideshow}
              data-testid="lightbox-slideshow"
            >
              {slideshow ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              <span className="captured-lb-slideshow-label">
                {slideshow ? 'Pause' : 'Slideshow'}
              </span>
            </button>

            {/* Prev */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="captured-lb-nav captured-lb-prev"
              aria-label="Previous photo"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-7 h-7" />
            </button>

            {/* Media — image or video, original aspect ratio preserved */}
            <AnimatePresence mode="wait">
              <motion.figure
                key={index}
                initial={{ opacity: 0, scale: 0.985 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.985 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="captured-lb-figure"
                onClick={(e) => e.stopPropagation()}
              >
                {current.type === 'video' ? (
                  <video
                    ref={videoRef}
                    src={current.url}
                    poster={current.thumb}
                    controls
                    autoPlay
                    playsInline
                    className="captured-lb-img"
                    data-testid="lightbox-video"
                  />
                ) : (
                  <picture>
                    {current.webp && <source srcSet={current.webp} type="image/webp" />}
                    <img
                      src={current.url}
                      alt={current.alt}
                      draggable={false}
                      onContextMenu={blockContext}
                      className="captured-lb-img"
                    />
                  </picture>
                )}
                <figcaption className="captured-lb-caption">
                  <span>{index + 1} / {photos.length}</span>
                  <span className="captured-lb-caption-sep">·</span>
                  <span className="captured-lb-caption-alt">{current.alt}</span>
                </figcaption>
              </motion.figure>
            </AnimatePresence>

            {/* Next */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="captured-lb-nav captured-lb-next"
              aria-label="Next photo"
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-7 h-7" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export { Gallery };
